import React, {useState} from "react";
import {connect} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {css, Interpolation, Theme} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {backgroundColor, borderRadius, mh, mv, p} from "@pg-design/helpers-css";
import {SearchIcon} from "@pg-design/icons-module";
import {FormActions, FormFieldType} from "@pg-mono/form";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {searchFormActions} from "../../search/actions/search_form_actions";
import {resetActiveDropdownItem} from "../../search/actions/set_active_dropdown_item";
import {ISearchInputValue} from "../../search/components/ISearchInputValue";
import {SearchAutocomplete} from "../../search/components/search_autocomplete/SearchAutocomplete";
import {searchFormFields} from "../../search/constants/search_form_constants";
import {SearchTab} from "../../search/types/SearchTab";
import {getStringQueryValuesFromFormValues} from "../../search/utils/get_string_values_from_form_values";
import {fetchAllVendorSearchLists, resetClientOnlyVendorSearchFormAtRoute} from "../actions/fetch_vendor_search_lists";
import {SearchVendorAutocompleteDropdown} from "./SearchVendorAutocompleteDropdown";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type IVendorSearchFormValuesType = Record<keyof typeof vendorSearchFormFields, any>;

interface IStateProps {
    formValues: IVendorSearchFormValuesType;
    region_name_reverted?: string | null;
}

// TODO HANDLE THESE TYPES
/* eslint-disable @typescript-eslint/no-explicit-any */
interface IActionsProps {
    // formActions: FormActions<IVendorSearchFormValuesType>;
    formActions: FormActions<Record<keyof typeof searchFormFields, any>>;
    resetClientOnlyVendorSearchFormAtRoute: any;
    resetActiveDropdownItem: typeof resetActiveDropdownItem;
    fetchAllVendorSearchLists: (searchInput: string) => Promise<SearchTab>;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

interface IOwnProps extends RouteComponentProps<{}> {
    latestQuery: Record<string, string | string[]>;
    onAfterSubmit?: () => void;
}
interface IProps extends IOwnProps, IStateProps, IActionsProps {}

export const vendorSearchFormFields = {
    search: FormFieldType.Input
};

const SearchVendorC = (props: IProps) => {
    const [isMobileSearchActive, setMobileSearchActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);

    /**
     * Callbacks
     */
    const onAfterTabSwitch = () => {
        props.resetActiveDropdownItem();
    };

    /**
     * Submit logic
     */

    const onSubmit = async (formValues: IVendorSearchFormValuesType) => {
        const searchValue = formValues["search"] as ISearchInputValue;
        if (searchValue.tabType === SearchTab.Vendors) {
            window.location.href = rpAppLink.vendor.detail.base({
                vendorSlug: searchValue.vendor.slug,
                vendorId: searchValue.vendor.id
            });
        }

        if (searchValue.tabType === SearchTab.Regions) {
            if (searchValue.regions.length === 0) {
                props.history.push(rpAppLink.vendor.list());
            } else {
                const regionSlug = searchValue.regions[0].slug;
                const url = rpAppLink.vendor.listFriendly({friendlySlug: regionSlug});
                props.history.push(url);
                props.onAfterSubmit && props.onAfterSubmit();
            }
        }
    };
    const onAfterChange = (name?: string, value?: ISearchInputValue | number | undefined) => {
        const currentValues = name ? {...props.formValues, [name]: value} : props.formValues;

        const queryValues = getStringQueryValuesFromFormValues(currentValues);

        //  submit only if user selected new region or vendor
        if (!queryValues.vendor && props.latestQuery.region_slug === currentValues.search.regions?.[0]?.slug) {
            return;
        }
        return onSubmit(currentValues);
    };

    const onSearchSubmit = () => onSubmit(props.formValues);

    const placeholder = "Miasto lub nazwa dewelopera";

    const inputIcon = (
        <div css={iconWrapper}>
            <SearchIcon size="2" css={inputIconSearchIcon} />
        </div>
    );

    const renderSearchAutocomplete = (
        onDropdownStatusChange: React.Dispatch<React.SetStateAction<boolean>>,
        isInModal?: boolean,
        inputCoreWrapperCss?: Interpolation<Theme>
    ) => {
        return (
            <div css={searchAutocompleteWrapper}>
                <SearchAutocomplete
                    values={props.formValues}
                    onChange={props.formActions.update}
                    onAfterChange={onAfterChange}
                    onSubmit={onSearchSubmit}
                    onDropdownStatusChange={onDropdownStatusChange}
                    onAfterTabSwitch={onAfterTabSwitch}
                    placeholder={placeholder}
                    disableMultiRegion
                    fetchAllSearchLists={props.fetchAllVendorSearchLists}
                    isInModal={isInModal}
                    autoFocus
                    hideArrowIcon
                    inputIcon={inputIcon}
                    renderDropdownList={(props) => (
                        <SearchVendorAutocompleteDropdown
                            dropdownIsOpen={props.dropdownIsOpen}
                            search={props.search}
                            selectedTab={props.selectedTab}
                            onLinkClick={props.onLinkClick}
                            requestStateObj={props.requestStateObj}
                            activeItem={props.activeItem}
                            offers={props.offers}
                            places={props.places}
                            regions={props.regions}
                            removeRegionTag={props.removeRegionTag}
                            vendors={props.vendors}
                            getFieldProps={props.getFieldProps}
                        />
                    )}
                    inputCoreWrapperCss={inputCoreWrapperCss}
                />
            </div>
        );
    };

    /**
     * Render
     */

    return (
        <div css={[backgroundColor("#fff")]}>
            <div onClick={() => setMobileSearchActive(true)} css={mobileInputTrigger}>
                {inputIcon}
                {props.region_name_reverted ? "Zmień lokalizację" : "Miasto lub nazwa dewelopera"}
            </div>

            <MobileOptionsModal isOpen={isMobileSearchActive} modalHeading="Wyszukiwanie deweloperów" onModalClose={() => setMobileSearchActive(false)}>
                {renderSearchAutocomplete(setMobileSearchActive, true)}
            </MobileOptionsModal>

            <DesktopBlend isActive={isDesktopSearchBlendActive} isFullScreen={false} top="13rem" heightDecrease="(13rem + 60px)" />

            <div css={searchDesktopWrapper}>{renderSearchAutocomplete(setDesktopSearchBlendActive, undefined, searchDesktopWrapperInputCoreWrapper)}</div>
        </div>
    );
};

export const SearchVendor = withRouter(connect(mapStateToProps, mapActionsToProps)(SearchVendorC));

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.search.formValues as IVendorSearchFormValuesType,
        region_name_reverted: state.vendor.list.region && state.vendor.list.region[0].full_name_reverted
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators(
            {
                resetClientOnlyVendorSearchFormAtRoute,
                resetActiveDropdownItem,
                fetchAllVendorSearchLists
            },
            dispatch
        )
    };
}

const searchDesktopWrapper = (theme: Theme) => css`
    position: relative;
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        ${mh("auto")};
        ${mv(0)};
        display: flex;
        align-items: center;
        flex-grow: 1;
        flex-shrink: 1;
        flex-wrap: wrap;
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        flex-wrap: unset;
        overflow-x: visible;
    }
`;

const searchDesktopWrapperInputCoreWrapper = css`
    ${borderRadius()}
`;

const mobileInputTrigger = (theme: Theme) => css`
    position: relative;
    display: flex;
    align-items: center;
    cursor: text;
    flex-basis: 100%;
    width: 100%;
    font-size: 1.4rem;
    height: 5rem;
    ${borderRadius()};
    border: 1px solid ${theme.colors.gray[600]};
    color: ${theme.colors.secondary};
    ${backgroundColor("#fff")};
    ${p(0, 2, 0, 5)};

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const iconWrapper = css`
    position: absolute;
    top: 1.5rem;
    left: 2.5rem;
    cursor: text;
    z-index: 1;
`;

const inputIconSearchIcon = (theme: Theme) => css`
    fill: ${theme.colors.secondary};
`;

const searchAutocompleteWrapper = (theme: Theme) => css`
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    height: calc(100% - 6rem);
    display: flex;
    flex-direction: column;

    input::placeholder {
        color: ${theme.colors.secondary};
    }

    @media (min-width: ${theme.breakpoints.sm}) {
        padding: 0;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        max-height: 100%;
        width: 100%;
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        width: calc(50% - 2rem);
    }
`;
import React, {useState} from "react";
import {connect} from "react-redux";
import {RouteComponentProps, withRouter} from "react-router";
import {css, Interpolation, Theme} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {backgroundColor, borderRadius, mh, mv, p} from "@pg-design/helpers-css";
import {SearchIcon} from "@pg-design/icons-module";
import {FormActions, FormFieldType} from "@pg-mono/form";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {DesktopBlend} from "../../atoms/DesktopBlend";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {searchFormActions} from "../../search/actions/search_form_actions";
import {resetActiveDropdownItem} from "../../search/actions/set_active_dropdown_item";
import {ISearchInputValue} from "../../search/components/ISearchInputValue";
import {SearchAutocomplete} from "../../search/components/search_autocomplete/SearchAutocomplete";
import {searchFormFields} from "../../search/constants/search_form_constants";
import {SearchTab} from "../../search/types/SearchTab";
import {getStringQueryValuesFromFormValues} from "../../search/utils/get_string_values_from_form_values";
import {fetchAllVendorSearchLists, resetClientOnlyVendorSearchFormAtRoute} from "../actions/fetch_vendor_search_lists";
import {SearchVendorAutocompleteDropdown} from "./SearchVendorAutocompleteDropdown";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type IVendorSearchFormValuesType = Record<keyof typeof vendorSearchFormFields, any>;

interface IStateProps {
    formValues: IVendorSearchFormValuesType;
    region_name_reverted?: string | null;
}

// TODO HANDLE THESE TYPES
/* eslint-disable @typescript-eslint/no-explicit-any */
interface IActionsProps {
    // formActions: FormActions<IVendorSearchFormValuesType>;
    formActions: FormActions<Record<keyof typeof searchFormFields, any>>;
    resetClientOnlyVendorSearchFormAtRoute: any;
    resetActiveDropdownItem: typeof resetActiveDropdownItem;
    fetchAllVendorSearchLists: (searchInput: string) => Promise<SearchTab>;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

interface IOwnProps extends RouteComponentProps<{}> {
    latestQuery: Record<string, string | string[]>;
    onAfterSubmit?: () => void;
}
interface IProps extends IOwnProps, IStateProps, IActionsProps {}

export const vendorSearchFormFields = {
    search: FormFieldType.Input
};

const SearchVendorC = (props: IProps) => {
    const [isMobileSearchActive, setMobileSearchActive] = useState(false);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);

    /**
     * Callbacks
     */
    const onAfterTabSwitch = () => {
        props.resetActiveDropdownItem();
    };

    /**
     * Submit logic
     */

    const onSubmit = async (formValues: IVendorSearchFormValuesType) => {
        const searchValue = formValues["search"] as ISearchInputValue;
        if (searchValue.tabType === SearchTab.Vendors) {
            window.location.href = rpAppLink.vendor.detail.base({
                vendorSlug: searchValue.vendor.slug,
                vendorId: searchValue.vendor.id
            });
        }

        if (searchValue.tabType === SearchTab.Regions) {
            if (searchValue.regions.length === 0) {
                props.history.push(rpAppLink.vendor.list());
            } else {
                const regionSlug = searchValue.regions[0].slug;
                const url = rpAppLink.vendor.listFriendly({friendlySlug: regionSlug});
                props.history.push(url);
                props.onAfterSubmit && props.onAfterSubmit();
            }
        }
    };
    const onAfterChange = (name?: string, value?: ISearchInputValue | number | undefined) => {
        const currentValues = name ? {...props.formValues, [name]: value} : props.formValues;

        const queryValues = getStringQueryValuesFromFormValues(currentValues);

        //  submit only if user selected new region or vendor
        if (!queryValues.vendor && props.latestQuery.region_slug === currentValues.search.regions?.[0]?.slug) {
            return;
        }
        return onSubmit(currentValues);
    };

    const onSearchSubmit = () => onSubmit(props.formValues);

    const placeholder = "Miasto lub nazwa dewelopera";

    const inputIcon = (
        <div css={iconWrapper}>
            <SearchIcon size="2" css={inputIconSearchIcon} />
        </div>
    );

    const renderSearchAutocomplete = (
        onDropdownStatusChange: React.Dispatch<React.SetStateAction<boolean>>,
        isInModal?: boolean,
        inputCoreWrapperCss?: Interpolation<Theme>
    ) => {
        return (
            <div css={searchAutocompleteWrapper}>
                <SearchAutocomplete
                    values={props.formValues}
                    onChange={props.formActions.update}
                    onAfterChange={onAfterChange}
                    onSubmit={onSearchSubmit}
                    onDropdownStatusChange={onDropdownStatusChange}
                    onAfterTabSwitch={onAfterTabSwitch}
                    placeholder={placeholder}
                    disableMultiRegion
                    fetchAllSearchLists={props.fetchAllVendorSearchLists}
                    isInModal={isInModal}
                    autoFocus
                    hideArrowIcon
                    inputIcon={inputIcon}
                    renderDropdownList={(props) => (
                        <SearchVendorAutocompleteDropdown
                            dropdownIsOpen={props.dropdownIsOpen}
                            search={props.search}
                            selectedTab={props.selectedTab}
                            onLinkClick={props.onLinkClick}
                            requestStateObj={props.requestStateObj}
                            activeItem={props.activeItem}
                            offers={props.offers}
                            places={props.places}
                            regions={props.regions}
                            removeRegionTag={props.removeRegionTag}
                            vendors={props.vendors}
                            getFieldProps={props.getFieldProps}
                        />
                    )}
                    inputCoreWrapperCss={inputCoreWrapperCss}
                />
            </div>
        );
    };

    /**
     * Render
     */

    return (
        <div css={[backgroundColor("#fff")]}>
            <div onClick={() => setMobileSearchActive(true)} css={mobileInputTrigger}>
                {inputIcon}
                {props.region_name_reverted ? "Zmień lokalizację" : "Miasto lub nazwa dewelopera"}
            </div>

            <MobileOptionsModal isOpen={isMobileSearchActive} modalHeading="Wyszukiwanie deweloperów" onModalClose={() => setMobileSearchActive(false)}>
                {renderSearchAutocomplete(setMobileSearchActive, true)}
            </MobileOptionsModal>

            <DesktopBlend isActive={isDesktopSearchBlendActive} isFullScreen={false} top="13rem" heightDecrease="(13rem + 60px)" />

            <div css={searchDesktopWrapper}>{renderSearchAutocomplete(setDesktopSearchBlendActive, undefined, searchDesktopWrapperInputCoreWrapper)}</div>
        </div>
    );
};

export const SearchVendor = withRouter(connect(mapStateToProps, mapActionsToProps)(SearchVendorC));

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.search.formValues as IVendorSearchFormValuesType,
        region_name_reverted: state.vendor.list.region && state.vendor.list.region[0].full_name_reverted
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators(
            {
                resetClientOnlyVendorSearchFormAtRoute,
                resetActiveDropdownItem,
                fetchAllVendorSearchLists
            },
            dispatch
        )
    };
}

const searchDesktopWrapper = (theme: Theme) => css`
    position: relative;
    display: none;

    @media (min-width: ${theme.breakpoints.md}) {
        ${mh("auto")};
        ${mv(0)};
        display: flex;
        align-items: center;
        flex-grow: 1;
        flex-shrink: 1;
        flex-wrap: wrap;
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        flex-wrap: unset;
        overflow-x: visible;
    }
`;

const searchDesktopWrapperInputCoreWrapper = css`
    ${borderRadius()}
`;

const mobileInputTrigger = (theme: Theme) => css`
    position: relative;
    display: flex;
    align-items: center;
    cursor: text;
    flex-basis: 100%;
    width: 100%;
    font-size: 1.4rem;
    height: 5rem;
    ${borderRadius()};
    border: 1px solid ${theme.colors.gray[600]};
    color: ${theme.colors.secondary};
    ${backgroundColor("#fff")};
    ${p(0, 2, 0, 5)};

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const iconWrapper = css`
    position: absolute;
    top: 1.5rem;
    left: 2.5rem;
    cursor: text;
    z-index: 1;
`;

const inputIconSearchIcon = (theme: Theme) => css`
    fill: ${theme.colors.secondary};
`;

const searchAutocompleteWrapper = (theme: Theme) => css`
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    height: calc(100% - 6rem);
    display: flex;
    flex-direction: column;

    input::placeholder {
        color: ${theme.colors.secondary};
    }

    @media (min-width: ${theme.breakpoints.sm}) {
        padding: 0;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        max-height: 100%;
        width: 100%;
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        width: calc(50% - 2rem);
    }
`;
